import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { QRCodeCanvas } from "qrcode.react";
import jsPDF from "jspdf";
import {
  ArrowLeft, FloppyDisk, Trash, Plus, X, FilePdf, Image as ImageIcon,
  CloudArrowUp, Copy, WhatsappLogo, FileText, QrCode, ArrowsClockwise,
  Tooth, Pill, Lock, Sparkle, Pulse, BellRinging, Cigarette,
} from "@phosphor-icons/react";
import FollowupAITab from "../components/FollowupAITab";
import DigitalTwinTab from "../components/DigitalTwinTab";
import ScheduledFollowupsTab from "../components/ScheduledFollowupsTab";

const CATEGORIES = [
  { value: "document", label: "Documento", icon: FilePdf },
  { value: "opt", label: "OPT", icon: ImageIcon },
  { value: "cbct", label: "CBCT", icon: ImageIcon },
  { value: "photo", label: "Foto intraorale", icon: ImageIcon },
];

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [files, setFiles] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const initialTab = new URLSearchParams(location.search).get("tab") || "details";
  const [tab, setTab] = useState(initialTab);
  const fileInputRef = useRef(null);
  const [uploadCategory, setUploadCategory] = useState("document");
  const [uploading, setUploading] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [copied, setCopied] = useState("");

  const portalBase = (settings.public_base_url && settings.public_base_url.replace(/\/+$/, "")) || window.location.origin;
  const portalUrl = patient ? `${portalBase}/portal/${patient.access_token}` : "";

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, fRes, sRes] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get(`/patients/${id}/files`),
        api.get(`/settings`),
      ]);
      setPatient(pRes.data);
      setFiles(fRes.data);
      setSettings(sRes.data || {});
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Clear the "NUOVA ATTIVITÀ" badge in the clinician Inbox as soon as the
    // patient scheda is opened (fire-and-forget — never blocks the UI).
    api.post(`/patients/${id}/mark-seen`).catch(() => {});
    /* eslint-disable-next-line */
  }, [id]);

  const update = (k, v) => setPatient((p) => ({ ...p, [k]: v }));

  const updateArray = (key, idx, field, value) => {
    setPatient((p) => {
      const arr = [...(p[key] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...p, [key]: arr };
    });
  };

  const addItem = (key, item) => setPatient((p) => ({ ...p, [key]: [...(p[key] || []), item] }));
  const removeItem = (key, idx) => setPatient((p) => {
    const arr = [...(p[key] || [])];
    arr.splice(idx, 1);
    return { ...p, [key]: arr };
  });

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = { ...patient };
      delete payload.id; delete payload.access_pin; delete payload.access_token;
      delete payload.created_at; delete payload.updated_at; delete payload.created_by; delete payload.is_deleted;
      const { data } = await api.patch(`/patients/${id}`, payload);
      setPatient(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const rotatePin = async () => {
    if (!window.confirm("Rigenerare il PIN? Il PIN precedente non sarà più valido.")) return;
    try {
      const { data } = await api.post(`/patients/${id}/rotate-pin`);
      setPatient({ ...patient, access_pin: data.access_pin });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const rotateAccess = async () => {
    if (!window.confirm("Rigenerare COMPLETAMENTE l'accesso paziente?\n\nVerranno generati un NUOVO link e un NUOVO PIN. Il link e il PIN precedenti non saranno più validi.\n\nDovrai inviare al paziente il nuovo link via WhatsApp.")) return;
    try {
      const { data } = await api.post(`/patients/${id}/rotate-access`);
      setPatient({ ...patient, access_token: data.access_token, access_pin: data.access_pin });
      setPortalTest(null);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const [portalTest, setPortalTest] = useState(null);
  const [portalTesting, setPortalTesting] = useState(false);
  const runPortalTest = async () => {
    setPortalTesting(true);
    try {
      const { data } = await api.get(`/patients/${id}/portal-test`);
      setPortalTest(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPortalTesting(false);
    }
  };

  const handleDelete = async (hard = false) => {
    try {
      await api.delete(`/patients/${id}${hard ? "?hard=true" : ""}`);
      navigate("/patients");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const onPickFiles = () => fileInputRef.current?.click();

  const onFilesChosen = async (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length === 0) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", uploadCategory);
        await api.post(`/patients/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      const { data } = await api.get(`/patients/${id}/files`);
      setFiles(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deleteFile = async (fid) => {
    if (!window.confirm("Eliminare questo file?")) return;
    try {
      await api.delete(`/files/${fid}`);
      setFiles(files.filter((f) => f.id !== fid));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(""), 1500);
      if (label === "url") {
        api.post("/track/portal_link_copied", { patient_id: id }).catch(() => {});
      }
    } catch (_) {}
  };

  const trackWhatsapp = () => {
    api.post("/track/whatsapp_shared", { patient_id: id }).catch(() => {});
  };

  const whatsappLink = () => {
    if (!patient) return "#";
    // Use the new Giorno 0 template if available; fall back to legacy single template.
    const tplFromSettings = (settings?.followup_templates && settings.followup_templates["0"]) || settings?.whatsapp_template;
    const template = tplFromSettings ||
      "Gentile {patient_name}, ecco il link al suo referto Mediadent:\n{patient_link}\n\nPIN: {patient_pin}";
    const link = portalUrl;
    const pin = patient.access_pin || "";
    const msg = template
      .replaceAll("{patient_name}", `${patient.first_name} ${patient.last_name}`)
      .replaceAll("{patient_link}", link)
      .replaceAll("{patient_pin}", pin)
      .replaceAll("{portal_link}", link)
      .replaceAll("{pin}", pin)
      .replaceAll("{clinic_name}", settings?.clinic_name || "Mediadent Sarzana")
      .replaceAll("{doctor_name}", settings?.doctor_name || "")
      .replaceAll("{procedure}", patient.intervention_type || "")
      .replaceAll("{followup_day}", "0");
    const phone = (patient.phone || "").replace(/\D/g, "");
    return phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
  };

  const exportPDF = () => {
    if (!patient) return;
    api.post("/track/pdf_exported", { patient_id: id }).catch(() => {});
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    let y = 56;

    // Header
    doc.setFillColor(44, 74, 59);
    doc.rect(0, 0, W, 80, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text(settings.clinic_name || "Mediadent Sarzana", 40, 36);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Referto post-operatorio", 40, 56);
    if (settings.address) doc.text(settings.address, 40, 70);

    y = 110;
    doc.setTextColor(28, 35, 33);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(`${patient.first_name} ${patient.last_name}`, 40, y);
    y += 18;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(86, 99, 93);
    const lines = [];
    if (patient.fiscal_code) lines.push(`Codice fiscale: ${patient.fiscal_code}`);
    if (patient.dob) lines.push(`Data di nascita: ${patient.dob}`);
    if (patient.phone) lines.push(`Telefono: ${patient.phone}`);
    if (patient.email) lines.push(`Email: ${patient.email}`);
    lines.forEach((l) => { doc.text(l, 40, y); y += 14; });

    y += 8;
    const section = (title, body) => {
      if (!body) return;
      doc.setTextColor(44, 74, 59);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(title.toUpperCase(), 40, y);
      y += 14;
      doc.setTextColor(28, 35, 33);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const split = doc.splitTextToSize(body, W - 80);
      split.forEach((l) => {
        if (y > 770) { doc.addPage(); y = 56; }
        doc.text(l, 40, y); y += 13;
      });
      y += 8;
    };

    section("Intervento", `${patient.intervention_type || ""} ${patient.intervention_date ? `(${patient.intervention_date})` : ""}\n${patient.intervention_details || ""}`.trim());
    section("Materiali utilizzati", patient.materials_used);
    section("Istruzioni post-operatorie", patient.post_op_instructions);

    if ((patient.implants || []).length) {
      doc.setTextColor(44, 74, 59);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("IMPIANTI", 40, y); y += 14;
      doc.setTextColor(28, 35, 33);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      (patient.implants || []).forEach((im, i) => {
        if (y > 770) { doc.addPage(); y = 56; }
        const txt = `${i + 1}. ${im.brand || "-"} • ${im.model || "-"} • Ø${im.diameter || "-"} × L${im.length || "-"} • Lotto ${im.lot || "-"}${im.position ? ` • Posizione ${im.position}` : ""}`;
        doc.text(txt, 40, y); y += 13;
      });
      y += 8;
    }

    if ((patient.medications || []).length) {
      doc.setTextColor(44, 74, 59);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("FARMACI", 40, y); y += 14;
      doc.setTextColor(28, 35, 33);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      (patient.medications || []).forEach((m, i) => {
        if (y > 770) { doc.addPage(); y = 56; }
        const txt = `${i + 1}. ${m.name || "-"} — ${m.dosage || ""} ${m.frequency || ""} ${m.duration ? `per ${m.duration}` : ""}`;
        doc.text(txt, 40, y); y += 13;
        if (m.notes) { doc.text(`   ${m.notes}`, 40, y); y += 13; }
      });
    }

    // Footer
    const footerY = doc.internal.pageSize.getHeight() - 40;
    doc.setDrawColor(230, 232, 230);
    doc.line(40, footerY - 16, W - 40, footerY - 16);
    doc.setFontSize(8);
    doc.setTextColor(130, 158, 142);
    doc.text(`Generato il ${new Date().toLocaleString("it-IT")} • care.mediadentsarzana.it`, 40, footerY);

    doc.save(`Referto_${patient.last_name}_${patient.first_name}.pdf`.replace(/\s+/g, "_"));
  };

  if (loading) return <div className="text-center text-[#56635D] py-12" data-testid="patient-loading">Caricamento…</div>;
  if (!patient) return <div className="text-center text-[#A84A4A] py-12">Paziente non trovato</div>;

  const TABS = [
    { key: "details", label: "Dettagli", icon: FileText },
    { key: "implants", label: "Impianti", icon: Tooth },
    { key: "medications", label: "Farmaci", icon: Pill },
    { key: "files", label: "File & Referti", icon: CloudArrowUp },
    { key: "scheduled", label: "Follow-up automatico", icon: BellRinging },
    { key: "followup", label: "Follow-up AI", icon: Sparkle },
    { key: "twin", label: "Gemello Digitale", icon: Pulse },
    { key: "share", label: "Accesso paziente", icon: QrCode },
  ];

  return (
    <div className="space-y-6 fade-up" data-testid="patient-detail-page">
      <div className="flex flex-col gap-4">
        <Link to="/patients" className="inline-flex items-center gap-2 text-sm text-brand-700 hover:text-brand-800 w-fit">
          <ArrowLeft size={16} weight="bold" /> Pazienti
        </Link>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-brand-400">Scheda paziente</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-[#1C2321] mt-2" data-testid="patient-name">
              {patient.first_name} {patient.last_name}
            </h1>
            <div className="flex flex-wrap gap-2 mt-3">
              {patient.fiscal_code && <span className="font-mono text-xs bg-[#F0F0EE] px-2.5 py-1 rounded-full">{patient.fiscal_code}</span>}
              {patient.phone && <span className="text-xs bg-[#F0F0EE] px-2.5 py-1 rounded-full">{patient.phone}</span>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={exportPDF} data-testid="export-pdf-button" className="inline-flex items-center gap-2 bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] px-4 py-2.5 rounded-lg text-sm">
              <FilePdf size={16} weight="duotone" /> Esporta PDF
            </button>
            <a href={whatsappLink()} target="_blank" rel="noreferrer" onClick={trackWhatsapp} data-testid="whatsapp-share-button" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebd5b] text-white px-4 py-2.5 rounded-lg text-sm">
              <WhatsappLogo size={16} weight="fill" /> WhatsApp
            </a>
            <button onClick={save} disabled={saving} data-testid="patient-save-button" className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 rounded-lg text-sm disabled:opacity-60">
              <FloppyDisk size={16} weight="bold" /> {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </div>
      </div>

      {location.state?.justCreated && (
        <div className="elev-card p-4 bg-brand-50 border-brand-200">
          <div className="text-sm text-brand-700">
            Paziente creato. Condividi link e PIN dal tab "Accesso paziente".
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto -mx-1 px-1 border-b border-[#E6E8E6]">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${
              tab === t.key ? "border-brand-700 text-brand-700" : "border-transparent text-[#56635D] hover:text-brand-700"
            }`}
          >
            <t.icon size={16} weight="duotone" /> {t.label}
          </button>
        ))}
      </div>

      {error && <div className="text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-3 py-2">{error}</div>}

      {/* Tab content */}
      {tab === "details" && (
        <div className="elev-card p-6 space-y-6">
          <Grid2>
            <FieldInput label="Nome" value={patient.first_name} onChange={(v) => update("first_name", v)} testId="edit-first-name" />
            <FieldInput label="Cognome" value={patient.last_name} onChange={(v) => update("last_name", v)} testId="edit-last-name" />
            <FieldInput label="Codice fiscale" value={patient.fiscal_code || ""} onChange={(v) => update("fiscal_code", v)} testId="edit-fiscal" />
            <FieldInput label="Data di nascita" type="date" value={patient.dob || ""} onChange={(v) => update("dob", v)} testId="edit-dob" />
            <FieldInput label="Telefono" value={patient.phone || ""} onChange={(v) => update("phone", v)} testId="edit-phone" />
            <FieldInput label="Email" type="email" value={patient.email || ""} onChange={(v) => update("email", v)} testId="edit-email" />
            <FieldInput label="Data intervento" type="date" value={patient.intervention_date || ""} onChange={(v) => update("intervention_date", v)} testId="edit-int-date" />
            <FieldInput label="Tipo intervento" value={patient.intervention_type || ""} onChange={(v) => update("intervention_type", v)} testId="edit-int-type" />
          </Grid2>
          <FieldTextarea label="Dettagli intervento" rows={3} value={patient.intervention_details || ""} onChange={(v) => update("intervention_details", v)} testId="edit-int-details" />
          <FieldTextarea label="Materiali utilizzati" rows={2} value={patient.materials_used || ""} onChange={(v) => update("materials_used", v)} testId="edit-materials" />
          <FieldTextarea label="Istruzioni post-operatorie" rows={4} value={patient.post_op_instructions || ""} onChange={(v) => update("post_op_instructions", v)} testId="edit-post-op" />
          <FieldTextarea label="Note interne" rows={2} value={patient.notes || ""} onChange={(v) => update("notes", v)} testId="edit-notes" />

          {patient.smoking_profile && (
            <SmokingProfileCard profile={patient.smoking_profile} />
          )}
        </div>
      )}

      {tab === "implants" && (
        <div className="space-y-3">
          {(patient.implants || []).map((im, i) => (
            <div key={i} className="elev-card p-5" data-testid={`implant-${i}`}>
              <div className="flex justify-between items-center mb-4">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-400">Impianto #{i + 1}</div>
                <button onClick={() => removeItem("implants", i)} data-testid={`remove-implant-${i}`} className="text-[#A84A4A] hover:text-red-700"><X size={16} weight="bold" /></button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <FieldInput label="Marca" value={im.brand || ""} onChange={(v) => updateArray("implants", i, "brand", v)} />
                <FieldInput label="Modello" value={im.model || ""} onChange={(v) => updateArray("implants", i, "model", v)} />
                <FieldInput label="Posizione" value={im.position || ""} onChange={(v) => updateArray("implants", i, "position", v)} />
                <FieldInput label="Diametro" value={im.diameter || ""} onChange={(v) => updateArray("implants", i, "diameter", v)} />
                <FieldInput label="Lunghezza" value={im.length || ""} onChange={(v) => updateArray("implants", i, "length", v)} />
                <FieldInput label="Lotto" value={im.lot || ""} onChange={(v) => updateArray("implants", i, "lot", v)} />
              </div>
            </div>
          ))}
          <button onClick={() => addItem("implants", { brand: "", model: "", diameter: "", length: "", lot: "", position: "" })} data-testid="add-implant-button" className="inline-flex items-center gap-2 bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] px-4 py-2.5 rounded-lg text-sm">
            <Plus size={16} weight="bold" /> Aggiungi impianto
          </button>
        </div>
      )}

      {tab === "medications" && (
        <div className="space-y-3">
          {(patient.medications || []).map((m, i) => (
            <div key={i} className="elev-card p-5" data-testid={`medication-${i}`}>
              <div className="flex justify-between items-center mb-4">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-400">Farmaco #{i + 1}</div>
                <button onClick={() => removeItem("medications", i)} data-testid={`remove-medication-${i}`} className="text-[#A84A4A]"><X size={16} weight="bold" /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldInput label="Nome farmaco" value={m.name || ""} onChange={(v) => updateArray("medications", i, "name", v)} />
                <FieldInput label="Posologia" value={m.dosage || ""} onChange={(v) => updateArray("medications", i, "dosage", v)} />
                <FieldInput label="Frequenza" value={m.frequency || ""} onChange={(v) => updateArray("medications", i, "frequency", v)} />
                <FieldInput label="Durata" value={m.duration || ""} onChange={(v) => updateArray("medications", i, "duration", v)} />
              </div>
              <FieldTextarea label="Note" rows={2} value={m.notes || ""} onChange={(v) => updateArray("medications", i, "notes", v)} />
            </div>
          ))}
          <button onClick={() => addItem("medications", { name: "", dosage: "", frequency: "", duration: "", notes: "" })} data-testid="add-medication-button" className="inline-flex items-center gap-2 bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] px-4 py-2.5 rounded-lg text-sm">
            <Plus size={16} weight="bold" /> Aggiungi farmaco
          </button>
        </div>
      )}

      {tab === "files" && (
        <div className="space-y-4">
          <div className="elev-card p-5">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1">
                <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-1.5">Categoria</label>
                <select value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)} data-testid="file-category-select" className="w-full px-3 py-2.5 border border-[#E6E8E6] rounded-lg bg-white">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <input ref={fileInputRef} type="file" multiple onChange={onFilesChosen} className="hidden" data-testid="file-input" />
              <button onClick={onPickFiles} disabled={uploading} data-testid="upload-file-button" className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-2.5 rounded-lg disabled:opacity-60">
                <CloudArrowUp size={18} weight="duotone" /> {uploading ? "Caricamento…" : "Carica file"}
              </button>
            </div>
            <p className="text-xs text-[#56635D] mt-3">PDF, immagini, OPT, CBCT. Max 25MB per file.</p>
          </div>

          {files.length === 0 ? (
            <div className="elev-card p-8 text-center text-[#56635D]">Nessun file caricato.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="files-list">
              {files.map((f) => {
                const isImg = (f.content_type || "").startsWith("image/");
                const url = `${API}/files/${f.id}/download?auth=${encodeURIComponent(localStorage.getItem("md_token") || "")}`;
                return (
                  <div key={f.id} className="elev-card p-4 flex items-center gap-4" data-testid={`file-${f.id}`}>
                    <div className="w-12 h-12 shrink-0 rounded-lg bg-brand-50 flex items-center justify-center">
                      {isImg ? <ImageIcon size={20} weight="duotone" className="text-brand-700" /> : <FilePdf size={20} weight="duotone" className="text-brand-700" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{f.original_filename}</div>
                      <div className="text-xs text-[#56635D] mt-0.5">
                        {(CATEGORIES.find((c) => c.value === f.category)?.label || f.category)} • {(f.size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                    <a href={url} target="_blank" rel="noreferrer" className="text-xs text-brand-700 hover:underline">Apri</a>
                    <button onClick={() => deleteFile(f.id)} data-testid={`delete-file-${f.id}`} className="text-[#A84A4A]"><Trash size={16} weight="duotone" /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "followup" && patient && (
        <FollowupAITab patient={patient} settings={settings} onAudit={() => {}} />
      )}

      {tab === "scheduled" && patient && (
        <ScheduledFollowupsTab patient={patient} />
      )}

      {tab === "twin" && patient && (
        <DigitalTwinTab patient={patient} />
      )}

      {tab === "share" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="elev-card p-6 flex flex-col items-center">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-4 self-start">QR Code accesso paziente</div>
            <div className="bg-white p-4 rounded-xl border border-[#E6E8E6]" data-testid="qr-code-wrapper">
              <QRCodeCanvas value={portalUrl} size={220} fgColor="#2C4A3B" bgColor="#FFFFFF" includeMargin={false} />
            </div>
            <div className="mt-5 w-full">
              <div className="text-xs uppercase tracking-[0.18em] text-[#56635D] mb-1.5">Link portale</div>
              <div className="flex items-center gap-2">
                <input readOnly value={portalUrl} data-testid="portal-url" className="flex-1 px-3 py-2 border border-[#E6E8E6] rounded-lg bg-[#F7F7F5] text-sm font-mono truncate" />
                <button onClick={() => copy("url", portalUrl)} data-testid="copy-portal-url" className="p-2.5 rounded-lg bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB]"><Copy size={16} weight="duotone" /></button>
              </div>
              {copied === "url" && <div className="text-xs text-brand-700 mt-1">Copiato</div>}
            </div>
          </div>

          <div className="elev-card p-6 space-y-5">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-2">PIN di accesso</div>
              <div className="flex items-center gap-3">
                <div className="font-heading font-bold text-3xl tracking-[0.4em] bg-brand-50 px-5 py-3 rounded-lg text-brand-700" data-testid="patient-pin">
                  {patient.access_pin || "—"}
                </div>
                <button onClick={() => copy("pin", patient.access_pin || "")} data-testid="copy-pin-button" className="p-2.5 rounded-lg bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB]"><Copy size={16} weight="duotone" /></button>
                <button onClick={rotatePin} data-testid="rotate-pin-button" className="p-2.5 rounded-lg bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB]"><ArrowsClockwise size={16} weight="duotone" /></button>
              </div>
              {copied === "pin" && <div className="text-xs text-brand-700 mt-1">Copiato</div>}
              <p className="text-xs text-[#56635D] mt-3 flex items-start gap-1.5"><Lock size={12} weight="duotone" className="mt-0.5" /> Il PIN è richiesto al paziente per visualizzare la scheda dal portale.</p>
            </div>

            <a href={whatsappLink()} target="_blank" rel="noreferrer" onClick={trackWhatsapp} data-testid="whatsapp-message-button" className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebd5b] text-white w-full px-4 py-3 rounded-lg">
              <WhatsappLogo size={18} weight="fill" /> Invia link + PIN su WhatsApp
            </a>
            <p className="text-xs text-[#56635D]">Il messaggio viene precompilato con nome paziente, link e PIN. Personalizzabile da Impostazioni.</p>

            {/* Admin tools: rotate full access + portal test */}
            <div className="pt-3 mt-3 border-t border-[#E6E8E6] space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-400">Strumenti amministratore</div>
              <div className="flex flex-wrap gap-2">
                <button onClick={rotateAccess} data-testid="rotate-access-button" className="inline-flex items-center gap-2 bg-[#FFF3E8] text-[#B45E2E] hover:bg-[#F8E5D4] px-3 py-2 rounded-lg text-sm">
                  <ArrowsClockwise size={14} weight="duotone" /> Rigenera accesso paziente
                </button>
                <button onClick={runPortalTest} disabled={portalTesting} data-testid="portal-test-button" className="inline-flex items-center gap-2 bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] px-3 py-2 rounded-lg text-sm disabled:opacity-60">
                  {portalTesting ? "Verifica…" : "Test patient portal"}
                </button>
              </div>
              {portalTest && (
                <div className={`mt-2 p-3 rounded-lg border ${portalTest.ok ? "bg-[#EAFBEF] border-[#1F8B4C]/30" : "bg-[#FAEBEB] border-[#A84A4A]/30"}`} data-testid="portal-test-result">
                  <div className={`text-xs uppercase tracking-[0.18em] mb-2 ${portalTest.ok ? "text-[#1F8B4C]" : "text-[#A84A4A]"}`}>
                    {portalTest.ok ? "✓ Portale OK" : "✗ Portale ha problemi"}
                  </div>
                  <ul className="text-xs space-y-1">
                    {(portalTest.checks || []).map((c, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className={c.ok ? "text-[#1F8B4C]" : "text-[#A84A4A]"}>{c.ok ? "✓" : "✗"}</span>
                        <span className="flex-1 text-[#1C2321]">{c.name}{c.detail ? ` — ${c.detail}` : ""}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Danger zone */}
      <div className="elev-card p-6 border-[#A84A4A]/30">
        <div className="text-xs uppercase tracking-[0.2em] text-[#A84A4A] mb-2">GDPR — Diritto all'oblio</div>
        <p className="text-sm text-[#56635D] mb-4">L'eliminazione standard nasconde il paziente. L'eliminazione definitiva (solo SuperAdmin) rimuove tutti i dati in modo irreversibile.</p>
        {!showDelete ? (
          <button onClick={() => setShowDelete(true)} data-testid="delete-patient-init" className="inline-flex items-center gap-2 bg-[#A84A4A]/10 text-[#A84A4A] hover:bg-[#A84A4A]/15 px-4 py-2 rounded-lg text-sm">
            <Trash size={16} weight="duotone" /> Elimina paziente…
          </button>
        ) : (
          <div className="flex flex-wrap gap-2">
            <button onClick={() => handleDelete(false)} data-testid="delete-patient-soft" className="bg-[#A84A4A]/10 text-[#A84A4A] hover:bg-[#A84A4A]/15 px-4 py-2 rounded-lg text-sm">Conferma eliminazione</button>
            {user?.role === "superadmin" && (
              <button onClick={() => handleDelete(true)} data-testid="delete-patient-hard" className="bg-[#A84A4A] text-white hover:bg-red-800 px-4 py-2 rounded-lg text-sm">Elimina definitivamente (GDPR)</button>
            )}
            <button onClick={() => setShowDelete(false)} className="px-4 py-2 rounded-lg text-sm text-[#56635D]">Annulla</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Grid2({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>; }

function FieldInput({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-1.5">{label}</label>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2.5 border border-[#E6E8E6] rounded-lg bg-white focus:border-brand-700" />
    </div>
  );
}

function FieldTextarea({ label, value, onChange, rows = 3, testId }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-1.5">{label}</label>
      <textarea data-testid={testId} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2.5 border border-[#E6E8E6] rounded-lg bg-white focus:border-brand-700" />
    </div>
  );
}

function SmokingProfileCard({ profile }) {
  const date = profile.confirmed_at ? new Date(profile.confirmed_at).toLocaleString("it-IT") : "";
  return (
    <div className={`rounded-lg border p-4 ${profile.is_smoker ? "bg-[#FFFAE9] border-[#8A6D1F]/30" : "bg-[#EAFBEF] border-[#1F8B4C]/30"}`} data-testid="smoking-profile-card">
      <div className={`text-xs uppercase tracking-[0.18em] mb-2 flex items-center gap-1.5 ${profile.is_smoker ? "text-[#8A6D1F]" : "text-[#1F8B4C]"}`}>
        <Cigarette size={12} weight="duotone" /> Profilo fumatore (dichiarato dal paziente)
      </div>
      {profile.is_smoker ? (
        <div className="text-sm text-[#1C2321]">
          <strong className="font-medium">Fumatore</strong> · {profile.cigarettes_per_day || 0} sigarette/die · {profile.smoked_24h ? "ha fumato nelle ultime 24h" : "non ha fumato in 24h"}
          {profile.cessation_protocol_active && (
            <div className="text-xs text-[#56635D] mt-1">Protocollo Citisina attivo nel portale paziente.</div>
          )}
        </div>
      ) : (
        <div className="text-sm text-[#1C2321]"><strong className="font-medium">Non fumatore</strong></div>
      )}
      {date && <div className="text-[10px] font-mono text-[#56635D] mt-2">Confermato il {date}</div>}
    </div>
  );
}
